import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronLeft, 
  ChevronRight, 
  Map as MapIcon, 
  Users, 
  Settings, 
  Database, 
  BarChart3,
  Globe,
  Zap,
  User as UserIcon,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  Activity,
  Layers,
  Search,
  Target,
  Radar,
  Eye,
  EyeOff,
  Plus,
  Download,
  Upload,
  Loader2,
  Clock,
  Brain,
  ScanLine,
  Navigation,
  HelpCircle,
  LogOut
} from 'lucide-react';
import { useAuth } from '../../store/AuthContext';
import axios from 'axios';
import { getApiUrl } from '../../services/api';
import BrandSettingsModal from './BrandSettingsModal';
import MyMapsHelpModal from '../map/MyMapsHelpModal';
import { BRAND_COLORS } from '../map/MapContainer';

const RoverIcon = ({ size = 20, color = "currentColor" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke={color} 
    strokeWidth="1.5" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    {/* Chassis */}
    <path d="M4 16h16l-2-6H6l-2 6z" />
    {/* Wheels */}
    <circle cx="6" cy="18" r="2" />
    <circle cx="12" cy="18" r="2" />
    <circle cx="18" cy="18" r="2" />
    {/* Mast/Neck */}
    <path d="M9 10L10 6" />
    {/* Head/Camera */}
    <rect x="9" y="3" width="5" height="3" rx="1" />
    {/* Instrument arm */}
    <path d="M15 10l2-2" />
  </svg>
);

// Etiqueta de marca: preserva acrónimos en mayúsculas (BK, KFC) y pasa el resto
// a Title Case, para agrupar y mostrar de forma consistente.
const brandLabel = (b) => {
  const s = String(b || '').trim() || 'Unbranded';
  return s === s.toUpperCase() ? s : s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

const Sidebar = ({
  onTurboClick,
  onBrandSelect,
  scoutActive,
  onScoutToggle,
  onRefreshNeeded,
  hiddenBrands,
  onToggleVisibility,
  onToggleCategoryVisibility,
  collapsed,
  onToggleCollapse,
  variant = 'fixed',
  open = false,
  onClose
}) => {
  const isDrawer = variant === 'drawer';
  const [mapsExpanded, setMapsExpanded] = useState(false);
  const [exchangeExpanded, setExchangeExpanded] = useState(false);
  const [myMapsHelpOpen, setMyMapsHelpOpen] = useState(false);
  const [sectionsExpanded, setSectionsExpanded] = useState({
    base: false,
    engine: false,
    competence: false
  });
  const [brands, setBrands] = useState({ base: [], engine: [], competence: [] });
  const [settingsModal, setSettingsModal] = useState({
    isOpen: false,
    brand: '',
    type: '',
    color: '#FF0000',
    icon: 'pushpin',
    letter: ''
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout } = useAuth();
  const [runtimeVersions, setRuntimeVersions] = useState({
    web: '--',
    api: '--',
    mongodb: '--'
  });

  // In drawer mode, close the drawer whenever the route changes (after a nav click).
  useEffect(() => {
    if (isDrawer && open && onClose) onClose();
    // eslint-disable-next-line
  }, [location.pathname]);

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // format: 'kml' (por defecto) o 'kmz' (ZIP nativo de Google Earth).
  const handleExportKml = async (format = 'kml') => {
    try {
      // Pass hidden brands as query param
      const hiddenArray = Array.from(hiddenBrands || []);
      const params = new URLSearchParams();
      if (hiddenArray.length > 0) {
        params.append('hiddenBrands', hiddenArray.join(','));
      }
      if (format === 'kmz') params.append('format', 'kmz');

      const resp = await axios.get(`${getApiUrl()}/exports/kml?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      downloadBlob(resp.data, `opportunity-export.${format}`);
    } catch (err) {
      alert(`Error exportando ${format.toUpperCase()}`);
    }
  };

  const handleExportKmlDriving = async (format = 'kml') => {
    try {
      const hiddenArray = Array.from(hiddenBrands || []);
      const params = new URLSearchParams();
      if (hiddenArray.length > 0) {
        params.append('hiddenBrands', hiddenArray.join(','));
      }
      if (format === 'kmz') params.append('format', 'kmz');

      const resp = await axios.get(`${getApiUrl()}/exports/kml-driving?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      downloadBlob(resp.data, `opportunity-mymaps.${format}`);

      // Google My Maps admite como máximo 10 capas por mapa. Si la exportación
      // genera más, avisamos para que el usuario filtre marcas o importe por partes.
      const layers = parseInt(resp.headers?.['x-layer-count'], 10);
      if (Number.isFinite(layers) && layers > 10) {
        alert(
          `La exportación tiene ${layers} capas y Google My Maps admite máximo 10 por mapa. ` +
          `Oculta algunas marcas (con el ojo en la leyenda) o crea varios mapas e impórtalas por partes.`
        );
      }
    } catch (err) {
      alert(`Error exportando ${format.toUpperCase()} para Google My Maps`);
    }
  };

  // Exporta un .zip con un .kmz por categoría (bases / locomotoras / competencia /
  // oportunidades), cada uno de ≤10 capas; los que sobran se numeran (_2, _3…).
  const handleExportKmlDrivingSplit = async () => {
    try {
      const hiddenArray = Array.from(hiddenBrands || []);
      const params = new URLSearchParams();
      if (hiddenArray.length > 0) {
        params.append('hiddenBrands', hiddenArray.join(','));
      }

      const resp = await axios.get(`${getApiUrl()}/exports/kml-driving-split?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      downloadBlob(resp.data, 'opportunity-mymaps-por-categoria.zip');

      const nFiles = parseInt(resp.headers?.['x-file-count'], 10);
      if (Number.isFinite(nFiles)) {
        alert(
          `Descargado un .zip con ${nFiles} archivo(s) .kmz (bases, locomotoras, competencia…). ` +
          `Descomprímelo e importa cada .kmz en un mapa de Google My Maps: cada uno tiene como mucho 10 capas.`
        );
      }
    } catch (err) {
      alert('Error exportando KMZ por categoría');
    }
  };

  // Excel (.xlsx) con todos los restaurantes y toda su información en columnas.
  const handleExportRestaurantsXlsx = async () => {
    try {
      const hiddenArray = Array.from(hiddenBrands || []);
      const params = new URLSearchParams();
      if (hiddenArray.length > 0) params.append('hiddenBrands', hiddenArray.join(','));

      const resp = await axios.get(`${getApiUrl()}/exports/restaurants?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      downloadBlob(resp.data, 'opportunity-restaurantes.xlsx');
    } catch (err) {
      alert('Error exportando Excel de restaurantes');
    }
  };

  // Excel (.xlsx) con las bases elegidas + competidor más cercano y conteos a 100/500 m.
  const handleExportBasesCompetitionXlsx = async () => {
    try {
      const hiddenArray = Array.from(hiddenBrands || []);
      const params = new URLSearchParams();
      if (hiddenArray.length > 0) params.append('hiddenBrands', hiddenArray.join(','));

      const resp = await axios.get(`${getApiUrl()}/exports/bases-competition?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      downloadBlob(resp.data, 'opportunity-bases-competencia.xlsx');
    } catch (err) {
      alert('Error exportando Excel de bases y competencia');
    }
  };

  const handleExportCarto = async () => {
    try {
      const resp = await axios.get(`${getApiUrl()}/exports/carto`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = new Blob([JSON.stringify(resp.data, null, 2)], { type: 'application/geo+json' });
      downloadBlob(blob, 'opportunity-carto.geojson');
    } catch (err) {
      alert('Error exportando GeoJSON');
    }
  };

  const handleImportCarto = async () => {
    const url = window.prompt('Pega la URL del GeoJSON exportado desde CARTO:');
    if (!url) return;
    const collection = window.prompt('Colección destino (base o competitor):', 'base') || 'base';
    try {
      await axios.post(`${getApiUrl()}/imports/carto`, {
        url,
        collection: collection === 'competitor' ? 'competitor' : 'base',
        type: 'base',
        brand: 'CARTO'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (onRefreshNeeded) onRefreshNeeded();
      alert('Importación desde CARTO completada');
    } catch (err) {
      alert('Error importando desde CARTO');
    }
  };

  useEffect(() => {
    if (token && mapsExpanded) {
      fetchBrands();
    }
  }, [token, mapsExpanded, user?.missionProfile]);

  useEffect(() => {
    if (!token) {
      setRuntimeVersions({ web: '--', api: '--', mongodb: '--' });
      return;
    }

    const fetchRuntimeVersions = async () => {
      try {
        const resp = await axios.get(`${getApiUrl()}/meta/versions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = resp?.data?.data || {};
        setRuntimeVersions({
          web: data.web || '--',
          api: data.api || '--',
          mongodb: data.mongodb || '--'
        });
      } catch (err) {
        setRuntimeVersions({ web: '--', api: '--', mongodb: '--' });
      }
    };

    fetchRuntimeVersions();
  }, [token]);

  const fetchBrands = async () => {
    // Utility to get consistent colors by type
    const getColorByType = (type, brandName = '') => {
      const redShades = ['#FF5252', '#E53935', '#C62828', '#B71C1C', '#FF8A80', '#FF1744'];
      const greenShades = ['#4CAF50', '#43A047', '#2E7D32', '#1B5E20', '#8BC34A', '#689F38'];
      
      const hash = (brandName || '').length;
      
      if (type === 'competence') return redShades[hash % redShades.length];
      if (type === 'engine') return greenShades[hash % greenShades.length];
      return '#0B3D91'; // Base Blue
    };

    try {
      const [baseResp, fleetResp] = await Promise.all([
        axios.get(`${getApiUrl()}/base`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${getApiUrl()}/ai/fleet`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      const fleetMap = (fleetResp.data || []).reduce((acc, f) => {
        acc[f.name.toLowerCase()] = f;
        return acc;
      }, {});

      const grouped = baseResp.data.reduce((acc, item) => {
        const type = item.type || 'base';
        const brandName = brandLabel(item.brand);
        
        if (!acc[type]) acc[type] = {};
        
        if (!acc[type][brandName]) {
          const brandKey = brandName.toLowerCase();
          const fleetInfo = fleetMap[brandKey];
          acc[type][brandName] = {
            name: brandName,
            // El color canónico por marca (BRAND_COLORS) manda, para que la leyenda
            // coincida con el mapa aunque los datos se importaran con el esquema viejo.
            color: BRAND_COLORS[item.source] || item.visuals?.color || getColorByType(type, brandName),
            icon: item.visuals?.icon || 'pushpin',
            letter: item.visuals?.letter || '',
            status: 'downloaded',
            isGlobal: !!fleetInfo,
            lastUpdate: fleetInfo?.lastDownloadSuccess,
            count: 0
          };
        }
        acc[type][brandName].count++;
        return acc;
      }, { base: {}, engine: {}, competence: {} });

      // Merge with AI Analyzed brands from missionProfile
      if (user?.missionProfile) {
        const { competence = [], engines = [], brands: missionBrands = [] } = user.missionProfile;
        
        // Handle competence
        competence.forEach((item, idx) => {
          let name = item.name;
          if (name.length > 0) {
            name = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
          }
          const brandKey = name.toLowerCase();
          if (!grouped.competence[name]) {
            const fleetInfo = fleetMap[brandKey];
            grouped.competence[name] = {
              name,
              color: getColorByType('competence', name),
              icon: 'clock',
              status: (fleetInfo?.status === 'downloaded' || fleetInfo?.status === 'downloading') ? fleetInfo.status : 'pending',
              isGlobal: true,
              lastUpdate: fleetInfo?.lastDownloadSuccess,
              count: 0
            };
          }
        });

        // Handle engines
        engines.forEach((item, idx) => {
          let name = item.name;
          if (name.length > 0) {
            name = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
          }
          const brandKey = name.toLowerCase();
          if (!grouped.engine[name]) {
            const fleetInfo = fleetMap[brandKey];
            grouped.engine[name] = {
              name,
              color: getColorByType('engine', name),
              icon: 'clock',
              status: (fleetInfo?.status === 'downloaded' || fleetInfo?.status === 'downloading') ? fleetInfo.status : 'pending',
              isGlobal: true,
              lastUpdate: fleetInfo?.lastDownloadSuccess,
              count: 0
            };
          }
        });

        // Handle main brands (base)
        missionBrands.forEach((rawName, idx) => {
          let name = rawName;
          if (name.length > 0) {
            name = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
          }
          const brandKey = name.toLowerCase();
          if (!grouped.base[name]) {
            const fleetInfo = fleetMap[brandKey];
            grouped.base[name] = {
              name,
              color: getColorByType('base', name),
              icon: 'pushpin',
              status: (fleetInfo?.status === 'downloaded' || fleetInfo?.status === 'downloading') ? fleetInfo.status : 'pending',
              isGlobal: true,
              lastUpdate: fleetInfo?.lastDownloadSuccess,
              count: 0
            };
          }
        });
      }

      setBrands({
        base: Object.values(grouped.base),
        engine: Object.values(grouped.engine),
        competence: Object.values(grouped.competence)
      });
    } catch (err) {
      console.error('Error fetching brands for sidebar:', err);
    }
  };

  const getAgeString = (date) => {
    if (!date) return '';
    const diff = Math.floor((new Date() - new Date(date)) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Hoy';
    return `hace ${diff}d`;
  };

  const menuItems = [
    { icon: Target, label: 'Oportunidades', path: '/opportunities' },
    { icon: Database, label: 'Fuentes de datos', path: '/intel' },
    { icon: BarChart3, label: 'Análisis PASTA V', path: '/dashboard/analysis' },
    { icon: Radar, label: 'Scouting', id: 'scout' },
  ];

  const adminItems = [
    { icon: Users, label: 'Personal', path: '/admin/users' },
    { icon: Settings, label: 'Configuración', path: '/admin/settings' },
    { icon: ScanLine, label: 'Parámetros Scout', path: '/admin/scout-params' },
    { icon: RoverIcon, label: 'Estado del sistema', path: '/admin/status' },
    { icon: Brain, label: 'Prompts IA', path: '/admin/prompts' },
  ];

  const toggleSection = (section) => {
    setSectionsExpanded(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const panelStyle = isDrawer
    ? {
        width: '280px',
        maxWidth: '85vw',
        backgroundColor: '#05070A',
        borderRight: '1px solid #0B3D91',
        height: 'calc(100vh - 60px)',
        position: 'fixed',
        top: '60px',
        left: 0,
        transform: open ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1300,
        overflowY: 'auto',
        overflowX: 'hidden',
        boxShadow: open ? '4px 0 30px rgba(0,0,0,0.6)' : 'none'
      }
    : {
        width: collapsed ? '60px' : '260px',
        backgroundColor: '#05070A',
        borderRight: '1px solid #0B3D91',
        height: 'calc(100vh - 60px)',
        position: 'fixed',
        top: '60px',
        left: 0,
        transition: 'width 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 900,
        overflowY: 'auto',
        overflowX: 'hidden'
      };

  return (
    <>
      {isDrawer && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: '60px 0 0 0',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1250,
            opacity: open ? 1 : 0,
            pointerEvents: open ? 'auto' : 'none',
            transition: 'opacity 0.3s ease',
          }}
        />
      )}
    <div style={panelStyle}>
      <div style={{ flex: 1, padding: '20px 0' }}>
        
        {/* MAPS EXPANDABLE */}
        <div
          onClick={() => {
            if (!collapsed) setMapsExpanded(!mapsExpanded);
            navigate('/dashboard');
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 20px',
            cursor: 'pointer',
            gap: '15px',
            transition: 'background 0.2s',
            color: window.location.pathname === '/dashboard' || mapsExpanded ? '#00CEC9' : 'white',
            backgroundColor: window.location.pathname === '/dashboard' ? 'rgba(0, 206, 201, 0.05)' : 'transparent',
            borderLeft: window.location.pathname === '/dashboard' ? '3px solid #00CEC9' : '3px solid transparent'
          }}
        >
          <MapIcon size={20} />
          {!collapsed && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
              <span style={{ fontSize: '12px', fontWeight: '600', letterSpacing: '1px' }}>Mapa y capas</span>
              {mapsExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </div>
          )}
        </div>

        {/* MAPS SUB-AREAS */}
        {!collapsed && mapsExpanded && (
          <div style={{ paddingLeft: '20px' }}>
            {['base', 'engine', 'competence'].map(type => (
              <div key={type}>
                <div 
                  onClick={() => toggleSection(type)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 20px',
                    cursor: 'pointer',
                    gap: '10px',
                    color: '#888',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    justifyContent: 'space-between'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {type === 'base' && <Layers size={14} />}
                    {type === 'engine' && <Activity size={14} />}
                    {type === 'competence' && <Target size={14} />}
                    <span>{type}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div 
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onToggleCategoryVisibility) {
                          onToggleCategoryVisibility(type, brands[type].map(b => b.name));
                        }
                      }}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        padding: '2px',
                        borderRadius: '4px',
                        backgroundColor: 'rgba(0, 206, 201, 0.05)',
                        color: brands[type].some(b => !hiddenBrands?.has(`${b.name}|${type}`)) ? '#00CEC9' : '#555'
                      }}
                    >
                      {brands[type].some(b => !hiddenBrands?.has(`${b.name}|${type}`)) ? <Eye size={12} /> : <EyeOff size={12} />}
                    </div>
                    <div 
                      onClick={(e) => {
                        e.stopPropagation();
                        // 1:1 Mapping for clarity in the modal
                        let collection = 'base';
                        if (type === 'engine') collection = 'engine';
                        if (type === 'competence') collection = 'competition';
                        
                        onTurboClick({ type, collection });
                      }}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        padding: '2px',
                        borderRadius: '4px',
                        backgroundColor: 'rgba(0, 206, 201, 0.1)',
                        color: '#00CEC9'
                      }}
                    >
                      <Plus size={12} />
                    </div>
                    {sectionsExpanded[type] ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                  </div>
                </div>

                {sectionsExpanded[type] && (
                  <div style={{ paddingLeft: '35px', paddingBottom: '10px' }}>
                    {brands[type].length > 0 ? brands[type].map(brandObj => {
                      const brand = brandObj.name;
                      const isVisible = !hiddenBrands?.has(`${brand}|${type}`);
                      return (
                        <div 
                          key={brand}
                          style={{
                            fontSize: '10px',
                            color: '#AAA',
                            padding: '4px 0',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSettingsModal({
                              isOpen: true,
                              brand,
                              type,
                              color: brandObj.color,
                              icon: brandObj.icon,
                              letter: brandObj.letter
                            });
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                            {brandObj.isGlobal ? (
                              <Brain size={10} color="#00CEC9" title="AI Global Fleet Brand" />
                            ) : (
                              <Search size={10} color={brandObj.color || "#0B3D91"} />
                            )}
                            <span style={{ 
                              color: isVisible ? '#F0F0F0' : '#555',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {brand} ({brandObj.count || 0})
                            </span>
                            {brandObj.lastUpdate && (
                              <span style={{ fontSize: '8px', color: '#555', marginLeft: 'auto' }}>
                                {getAgeString(brandObj.lastUpdate)}
                              </span>
                            )}
                          </div>
                          
                          <div 
                            onClick={(e) => {
                              e.stopPropagation();
                              if (brandObj.status === 'downloaded' && onToggleVisibility) {
                                onToggleVisibility(brand, type);
                              }
                            }}
                            style={{ 
                              padding: '2px', 
                              borderRadius: '4px',
                              backgroundColor: isVisible ? 'rgba(0, 206, 201, 0.1)' : 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              cursor: brandObj.status === 'downloaded' ? 'pointer' : 'default'
                            }}
                          >
                            {brandObj.status === 'pending' ? (
                              <Loader2 size={12} color="#E27B58" className="animate-spin" />
                            ) : isVisible ? (
                              <Eye size={12} color="#00CEC9" />
                            ) : (
                              <EyeOff size={12} color="#555" />
                            )}
                          </div>
                        </div>
                      );
                    }) : (
                      <div style={{ fontSize: '9px', color: '#555', fontStyle: 'italic' }}>No search results</div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <BrandSettingsModal
          isOpen={settingsModal.isOpen}
          brand={settingsModal.brand}
          currentType={settingsModal.type}
          currentColor={settingsModal.color}
          currentIcon={settingsModal.icon}
          currentLetter={settingsModal.letter}
          onClose={() => setSettingsModal({ ...settingsModal, isOpen: false })}
          onUpdate={() => {
            fetchBrands();
            if (onRefreshNeeded) onRefreshNeeded();
          }}
        />

        <MyMapsHelpModal
          isOpen={myMapsHelpOpen}
          onClose={() => setMyMapsHelpOpen(false)}
        />

        {!collapsed && (
          <div style={{ padding: '14px 20px 4px', fontSize: '10px', color: '#555', letterSpacing: '2px', fontWeight: 'bold' }}>
            PRINCIPAL
          </div>
        )}

        {menuItems.map((item, index) => (
          <div
            key={index}
            onClick={() => {
              if (item.id === 'scout') {
                onScoutToggle();
              } else {
                navigate(item.path);
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px 20px',
              cursor: 'pointer',
              gap: '15px',
              transition: 'background 0.2s',
              color: (() => {
                const active = (item.id === 'scout' && scoutActive) || window.location.pathname.startsWith(item.path || '__none__');
                if (!active) return 'white';
                return item.path === '/opportunities' ? '#E27B58' : '#00CEC9';
              })(),
              backgroundColor: (() => {
                const active = (item.id === 'scout' && scoutActive) || window.location.pathname.startsWith(item.path || '__none__');
                if (!active) return 'transparent';
                return item.path === '/opportunities' ? 'rgba(226,123,88,0.05)' : 'rgba(0,206,201,0.05)';
              })(),
              borderLeft: (() => {
                const active = (item.id === 'scout' && scoutActive) || window.location.pathname.startsWith(item.path || '__none__');
                if (!active) return '3px solid transparent';
                return item.path === '/opportunities' ? '3px solid #E27B58' : '3px solid #00CEC9';
              })()
            }}
          >
            <item.icon size={20} />
            {!collapsed && <span style={{ fontSize: '12px', fontWeight: '600', letterSpacing: '1px' }}>{item.label}</span>}
          </div>
        ))}

        {/* HERRAMIENTAS */}
        {!collapsed && (
          <div style={{ padding: '14px 20px 4px', fontSize: '10px', color: '#555', letterSpacing: '2px', fontWeight: 'bold' }}>
            HERRAMIENTAS
          </div>
        )}

        {/* EXCHANGE SECTION */}
        <div
          onClick={() => {
            if (!collapsed) setExchangeExpanded(!exchangeExpanded);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 20px',
            cursor: 'pointer',
            gap: '15px',
            transition: 'background 0.2s',
            color: exchangeExpanded ? '#E27B58' : 'white',
            backgroundColor: exchangeExpanded ? 'rgba(226, 123, 88, 0.05)' : 'transparent',
            borderLeft: exchangeExpanded ? '3px solid #E27B58' : '3px solid transparent'
          }}
        >
          <Database size={20} />
          {!collapsed && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
              <span style={{ fontSize: '12px', fontWeight: '600', letterSpacing: '1px' }}>Intercambio</span>
              {exchangeExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </div>
          )}
        </div>

        {exchangeExpanded && !collapsed && (
          <div style={{ backgroundColor: 'rgba(0,0,0,0.2)', paddingBottom: '10px' }}>
            <div
              onClick={() => handleExportKmlDriving('kml')}
              className="sidebar-sub-item"
              style={{ padding: '10px 20px 10px 55px', fontSize: '11px', color: '#00CEC9', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}
            >
              <Navigation size={14} />
              <span>EXPORTAR PARA GOOGLE MY MAPS (por categoría + oportunidades)</span>
            </div>
            <div
              onClick={() => handleExportKmlDriving('kmz')}
              className="sidebar-sub-item"
              style={{ padding: '8px 20px 10px 55px', fontSize: '11px', color: '#00CEC9', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Download size={14} />
              <span>EXPORTAR MY MAPS COMO KMZ (Google Earth)</span>
            </div>
            <div
              onClick={handleExportKmlDrivingSplit}
              className="sidebar-sub-item"
              style={{ padding: '8px 20px 10px 55px', fontSize: '11px', color: '#00CEC9', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Download size={14} />
              <span>EXPORTAR KMZ POR CATEGORÍA (bases · locomotoras · competencia, ≤10 capas y ≤2.000 pts c/u)</span>
            </div>
            <div
              onClick={() => setMyMapsHelpOpen(true)}
              className="sidebar-sub-item"
              style={{ padding: '6px 20px 10px 55px', fontSize: '10px', color: '#888', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <HelpCircle size={13} />
              <span>¿CÓMO VERLO EN EL MÓVIL?</span>
            </div>
            <div
              onClick={() => handleExportKml('kml')}
              className="sidebar-sub-item"
              style={{ padding: '10px 20px 10px 55px', fontSize: '11px', color: '#BBB', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Download size={14} />
              <span>EXPORTAR KML (solo los visibles)</span>
            </div>
            <div
              onClick={() => handleExportKml('kmz')}
              className="sidebar-sub-item"
              style={{ padding: '8px 20px 10px 55px', fontSize: '11px', color: '#BBB', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Download size={14} />
              <span>EXPORTAR KMZ (solo los visibles)</span>
            </div>
            <div
              onClick={handleExportRestaurantsXlsx}
              className="sidebar-sub-item"
              style={{ padding: '10px 20px 10px 55px', fontSize: '11px', color: '#2ECC71', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}
            >
              <Download size={14} />
              <span>EXPORTAR EXCEL — TODOS LOS RESTAURANTES</span>
            </div>
            <div
              onClick={handleExportBasesCompetitionXlsx}
              className="sidebar-sub-item"
              style={{ padding: '10px 20px 10px 55px', fontSize: '11px', color: '#2ECC71', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}
            >
              <Download size={14} />
              <span>EXPORTAR EXCEL — BASES + COMPETENCIA (más cercana, 100m, 500m)</span>
            </div>
            <div
              onClick={handleExportCarto}
              className="sidebar-sub-item"
              style={{ padding: '10px 20px 10px 55px', fontSize: '11px', color: '#BBB', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Download size={14} />
              <span>EXPORTAR CARTO (GEOJSON)</span>
            </div>
            <div 
              onClick={handleImportCarto}
              className="sidebar-sub-item"
              style={{ padding: '10px 20px 10px 55px', fontSize: '11px', color: '#BBB', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Upload size={14} />
              <span>IMPORTAR DESDE CARTO</span>
            </div>
          </div>
        )}

        <div
          onClick={onTurboClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 20px',
            cursor: 'pointer',
            gap: '15px',
            transition: 'background 0.2s',
            color: '#00CEC9',
            marginTop: '10px'
          }}
        >
          <Zap size={20} />
          {!collapsed && <span style={{ fontSize: '12px', fontWeight: '800', letterSpacing: '2px' }}>Turbo Import</span>}
        </div>

        {/* CUENTA */}
        {!collapsed && (
          <div style={{ padding: '14px 20px 4px', fontSize: '10px', color: '#555', letterSpacing: '2px', fontWeight: 'bold' }}>
            CUENTA
          </div>
        )}
        <div
          onClick={() => navigate('/profile')}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 20px',
            cursor: 'pointer',
            gap: '15px',
            transition: 'background 0.2s',
            color: window.location.pathname === '/profile' ? '#00CEC9' : 'white',
            backgroundColor: window.location.pathname === '/profile' ? 'rgba(0, 206, 201, 0.05)' : 'transparent',
            borderLeft: window.location.pathname === '/profile' ? '3px solid #00CEC9' : '3px solid transparent'
          }}
        >
          <UserIcon size={20} />
          {!collapsed && <span style={{ fontSize: '12px', fontWeight: '600', letterSpacing: '1px' }}>Perfil</span>}
        </div>

        {/* Cerrar sesión (útil sobre todo en el cajón móvil) */}
        <div
          onClick={logout}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 20px',
            cursor: 'pointer',
            gap: '15px',
            transition: 'background 0.2s',
            color: '#E27B58',
          }}
        >
          <LogOut size={20} />
          {!collapsed && <span style={{ fontSize: '12px', fontWeight: '600', letterSpacing: '1px' }}>Cerrar sesión</span>}
        </div>

        {/* ADMIN SECTION */}
        {user?.role === 'admin' && (
          <div style={{ marginTop: '20px', borderTop: '1px solid rgba(11, 61, 145, 0.3)', paddingTop: '10px' }}>
            {!collapsed && (
              <div style={{ padding: '5px 20px', fontSize: '10px', color: '#555', letterSpacing: '2px', fontWeight: 'bold' }}>
                ADMINISTRACIÓN
              </div>
            )}
            {adminItems.map((item) => (
              <div
                key={item.path}
                onClick={() => navigate(item.path)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 20px',
                  cursor: 'pointer',
                  gap: '15px',
                  transition: 'background 0.2s',
                  color: window.location.pathname === item.path ? '#00CEC9' : 'white',
                  backgroundColor: window.location.pathname === item.path ? 'rgba(0, 206, 201, 0.05)' : 'transparent',
                  borderLeft: window.location.pathname === item.path ? '3px solid #00CEC9' : '3px solid transparent'
                }}
              >
                <item.icon size={20} />
                {!collapsed && <span style={{ fontSize: '12px', fontWeight: '600', letterSpacing: '1px' }}>{item.label}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ padding: '20px' }}>
        <div style={{
          padding: '10px',
          border: '1px solid rgba(226, 123, 88, 0.3)',
          backgroundColor: 'rgba(226, 123, 88, 0.05)',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <Globe size={18} color="#E27B58" />
          {!collapsed && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ fontSize: '10px', color: '#E27B58' }}>SECTOR: MARS-OPPORTUNITY</span>
              <span style={{ fontSize: '9px', color: 'rgba(226, 123, 88, 0.85)' }}>
                WEB v{runtimeVersions.web} | API v{runtimeVersions.api} | MONGODB v{runtimeVersions.mongodb}
              </span>
            </div>
          )}
        </div>
      </div>

      {!isDrawer && (
        <button
          onClick={onToggleCollapse}
          style={{
            position: 'absolute',
            right: '-12px',
            top: '20px',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: '#0B3D91',
            border: '1px solid #00CEC9',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 1001
          }}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      )}
    </div>
    </>
  );
};

export default Sidebar;
